/**
 * @file EnhancedTestResultsPanel.jsx
 * @description Sliding bottom panel that displays run and submission results.
 *              Contains two tabs: "Sample Tests" (local run) and "Submission" (full grading).
 *              Shows real-time progress bars, expandable test case details,
 *              compilation/runtime error output, and acceptance statistics.
 *
 * @module components/EnhancedTestResultsPanel
 */
import React, { useState, useRef } from 'react';
import {
    FiPlay,
    FiSend,
    FiMaximize2,
    FiMinimize2,
    FiX,
    FiCheck,
    FiAlertTriangle,
    FiClock,
    FiBarChart,
    FiTrendingUp,
    FiCopy
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * A highly interactive, sliding panel that displays the results of code execution.
 * It has two tabs: "Sample Tests" for local run results and "Submission" for 
 * final submission grading (including hidden test cases).
 * 
 * @param {Object} props
 * @param {Array|null} props.testResults - Results from a "Run" action (sample test cases).
 * @param {Object|null} props.submissionResult - Detailed grading results from a "Submit" action.
 * @param {Object} props.submissionProgress - State tracking the progress of batch test execution.
 * @param {boolean} props.isRunning - Whether a "Run" action is active.
 * @param {boolean} props.isSubmitting - Whether a "Submit" action is active.
 * @param {boolean} props.isMinimized - Controls if the panel is collapsed to a slim header.
 * @param {Function} props.onMinimize - Callback to toggle the minimized state.
 * @param {Function} props.onClose - Callback to completely dismiss the panel.
 * @param {string} props.activeTab - Currently active tab ('tests' or 'submission').
 * @param {Function} props.setActiveTab - State setter for the active tab.
 * @returns {JSX.Element}
 */
const EnhancedTestResultsPanel = ({
    testResults,
    submissionResult,
    submissionProgress,
    isRunning,
    isSubmitting,
    isMinimized,
    onMinimize,
    onClose,
    activeTab,
    setActiveTab
}) => {
    return (
        <div className="h-full flex flex-col bg-transparent overflow-hidden">
            {/* Unified Header Layer */}
            <div className="shrink-0 bg-emerald-800 relative z-50">
                <div className="h-12 flex items-center justify-between px-4 relative overflow-hidden">
                    {/* Status Info (Visible when minimized) */}
                    <motion.div
                        animate={{ 
                            opacity: isMinimized ? 1 : 0,
                            x: isMinimized ? 0 : -20,
                            pointerEvents: isMinimized ? 'auto' : 'none'
                        }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center space-x-2"
                    >
                        <div className={`w-2 h-2 rounded-full ${isRunning || isSubmitting ? 'bg-gray-300 animate-pulse' : 'bg-gray-300'}`}></div>
                        <span className="text-sm font-medium text-gray-300">
                            {isRunning ? 'Running Tests...' : isSubmitting ? 'Submitting...' : 'Results Panel'}
                        </span>
                        {submissionProgress.status === 'running' && (
                            <span className="text-xs text-gray-300 pl-1">
                                {submissionProgress.current}/{submissionProgress.total}
                            </span>
                        )}
                    </motion.div>

                    {/* Navigation Tabs (Visible when maximized) */}
                    <motion.div
                        animate={{ 
                            opacity: isMinimized ? 0 : 1,
                            y: isMinimized ? 40 : 0,
                            pointerEvents: isMinimized ? 'none' : 'auto'
                        }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute bottom-0 left-1 flex items-end space-x-[1px]"
                    >
                        <button
                            onClick={() => setActiveTab('tests')}
                            className={`relative min-w-[150px] px-4 py-2 text-sm font-semibold tracking-tight transition-all duration-300 rounded-t-lg flex items-center justify-center gap-2 z-10 ${activeTab === 'tests' ? 'text-emerald-800' : 'text-gray-300 hover:text-white'}`}
                        >
                            {activeTab === 'tests' && (
                                <motion.div layoutId="active-tab" className="absolute inset-0 bg-accent rounded-t-lg -z-10" />
                            )}
                            <FiPlay className={activeTab === 'tests' ? 'text-emerald-700 font-semibold' : ''} />
                            <span>Sample Tests</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('submission')}
                            className={`relative min-w-[150px] px-6 py-2 text-sm font-semibold tracking-tight transition-all duration-300 rounded-t-lg flex items-center justify-center gap-2 z-10 ${activeTab === 'submission' ? 'text-emerald-800' : 'text-gray-300 hover:text-white'}`}
                        >
                            {activeTab === 'submission' && (
                                <motion.div layoutId="active-tab" className="absolute inset-0 bg-accent rounded-t-lg -z-10" />
                            )}
                            <FiSend className={activeTab === 'submission' ? 'text-emerald-700 font-semibold' : ''} />
                            <span>Submission</span>
                        </button>
                    </motion.div>

                    {/* Controls (Always Visible) */}
                    <div className="ml-auto flex items-center space-x-2 z-20">
                        <button onClick={onMinimize} className="p-1.5 text-gray-300 hover:text-white rounded transition-colors" title={isMinimized ? "Maximize" : "Minimize"}>
                            {isMinimized ? <FiMaximize2 className="text-sm" /> : <FiMinimize2 className="text-sm" />}
                        </button>
                        <button onClick={onClose} disabled={isRunning || isSubmitting} className="p-1 text-gray-300 hover:text-white rounded transition-colors" title="Close">
                            <FiX className="text-sm" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Rolling Body Content */}
            <div className="flex-1 overflow-hidden relative">
                <motion.div
                    animate={{ 
                        opacity: isMinimized ? 0 : 1,
                        y: isMinimized ? 40 : 0
                    }}
                    transition={{ 
                        duration: 0.4, 
                        ease: [0.16, 1, 0.3, 1] 
                    }}
                    className="h-full w-full"
                >
                    <div className="h-full overflow-y-auto p-4 bg-accent thin-scrollbar">
                        {activeTab === 'tests' && (
                            <div className="space-y-4">
                                <SampleTestsContent testResults={testResults} isRunning={isRunning} />
                            </div>
                        )}
                        {activeTab === 'submission' && (
                            <div className="space-y-4">
                                <SubmissionContent
                                    submissionResult={submissionResult}
                                    submissionProgress={submissionProgress}
                                    isSubmitting={isSubmitting}
                                />
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

/**
 * @component SampleTestsContent
 * @description Renders sample test results: loading spinner, empty state, compilation errors, or per-case results.
 */
const SampleTestsContent = ({ testResults, isRunning }) => {
    if (isRunning) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Running Sample Tests</h3>
                    <p className="text-muted-foreground">Testing your solution against sample inputs...</p>
                </div>
            </div>
        );
    }

    if (testResults.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                <FiPlay className="text-5xl mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Test Results Yet</h3>
                <p className="text-muted-foreground mb-1">Run your code to see test results</p>
                <p className="text-sm text-muted-foreground">
                    Click "Run" to test your solution with sample inputs
                </p>
            </div>
        );
    }

    const hasCompilationError = testResults.length > 0 && testResults[0].error &&
        (testResults[0].error.includes('Compilation Error') ||
            testResults[0].error.includes('Runtime Error') ||
            testResults[0].error.includes('System Error'));

    if (hasCompilationError) {
        return (
            <div className="space-y-4">
                <div className="bg-white border-l-4 border-red-600 p-4 rounded">
                    <div className="flex items-center space-x-2 mb-3">
                        {/* <FiAlertTriangle className="text-red-600 text-lg" /> */}
                        <h4 className="font-medium text-red-600">
                            {testResults[0].error.includes('Compilation') ? 'Compilation Error' :
                                testResults[0].error.includes('Runtime') ? 'Runtime Error' : 'System Error'}
                        </h4>
                    </div>
                    <div className="font-mono text-sm text-red-600 text-left whitespace-pre-wrap">
                        {testResults[0].error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="bg-accent border-b border-gray-300 py-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-foreground flex items-center space-x-2">
                        <span>Sample Test Cases Results</span>
                    </h4>
                    <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${testResults.every(r => r.passed) ? 'bg-emerald-600' : 'bg-red-600'}`}></div>
                        <span className="text-sm font-medium text-foreground">
                            {testResults.every(r => r.passed) ? 'All Passed' : 'Some Failed'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6 text-sm">
                        <span className="text-green-600 flex items-center space-x-1">
                            <FiCheck className="w-3 h-3" />
                            <span>{testResults.filter(r => r.passed).length} Passed</span>
                        </span>
                        <span className="text-red-600 flex items-center space-x-1">
                            <FiX className="w-3 h-3" />
                            <span>{testResults.filter(r => !r.passed).length} Failed</span>
                        </span>
                    </div>

                    <div className="text-sm text-muted-foreground">
                        {testResults.length} Test Case{testResults.length !== 1 ? 's' : ''}
                    </div>
                </div>

                <div className="w-full bg-gray-300 rounded-full h-1 mt-3">
                    <div
                        className={`h-1 rounded-full transition-all duration-500 ${testResults.every(r => r.passed) ? 'bg-emerald-600' : 'bg-red-600'}`}
                        style={{
                            width: `${(testResults.filter(r => r.passed).length / testResults.length) * 100}%`
                        }}
                    />
                </div>
            </div>

            <div className="space-y-3">
                {testResults.map((result, index) => (
                    <TestCaseResult key={index} result={result} index={index} />
                ))}
            </div>
        </div>
    );
};

/**
 * @component TestCaseResult
 * @description Expandable card for a single test case showing input/expected/actual output.
 */
const TestCaseResult = ({ result, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const contentRef = useRef(null);

    const copyTestCase = async () => {
        const testCaseData = `Input:
${result.input}
    
Expected:
${result.expected}
    
Your Output:
${result.actual || 'No output'}`;

        try {
            await navigator.clipboard.writeText(testCaseData);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
        }
    };

    return (
        <div
            className={`rounded overflow-hidden transition-all duration-200 ${result.passed
                ? 'bg-white border-l-4 border-emerald-600 hover:bg-gray-50'
                : 'bg-white border-l-4 border-red-600 hover:bg-gray-50'
                }`}
        >
            <div className="p-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div>
                            <div className='flex items-center space-x-2 mb-1'>
                                <h5 className="font-medium text-foreground">Test Case {index + 1}</h5>
                                {copied ? (
                                    <FiCheck className="text-emerald-600" />
                                ) : (
                                    !result.is_test_mode_result && <FiCopy className="w-4 h-4 cursor-pointer text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); copyTestCase(); }} title="Copy test case" />
                                )}
                            </div>
                            <div className="flex items-center space-x-2 text-sm">
                                <span className={`${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                                    {result.passed ? 'Passed' : 'Failed'}
                                </span>
                                {result.executionTime && (
                                    <>
                                        <span className="text-muted-foreground">•</span>
                                        <span className="text-muted-foreground flex items-center space-x-1">
                                            <FiClock className="w-3 h-3" />
                                            <span>{result.executionTime}</span>
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        {result.passed ? (
                            <FiCheck className="text-green-600 text-lg" />
                        ) : (
                            <FiX className="text-red-600 text-lg" />
                        )}
                    </div>
                </div>
            </div>

            <div
                ref={contentRef}
                className="border-t border-gray-300 overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                    maxHeight: isExpanded ? (contentRef.current?.scrollHeight || 500) + 'px' : '0px',
                    opacity: isExpanded ? 1 : 0,
                    borderTopWidth: isExpanded ? '1px' : '0px'
                }}
            >
                <div className="p-4 space-y-3 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                            <span className="text-muted-foreground block mb-1 flex items-center space-x-1">
                                <span>Input:</span>
                            </span>
                            <div className="text-amber-600 break-words whitespace-pre-wrap text-left truncate">
                                {result.input}
                            </div>
                        </div>
                        <div>
                            <span className="text-muted-foreground block mb-1 flex items-center space-x-1">
                                <span>Expected:</span>
                            </span>
                            <div className="text-cyan-600 break-words text-left truncate">
                                {result.expected}
                            </div>
                        </div>
                        <div>
                            <span className="text-muted-foreground block mb-1 flex whitespace-pre-wrap items-center space-x-1">
                                <span>Your Output:</span>
                            </span>
                            <div className={`truncate break-words text-left ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                                {result.actual || 'No output'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * @component SubmissionContent
 * @description Orchestrates the submission tab: progress bar + final result or empty state.
 */
const SubmissionContent = ({ submissionResult, submissionProgress, isSubmitting }) => {
    return (
        <div className="space-y-4">
            {(submissionProgress?.status !== 'idle' || isSubmitting) && (
                <div className="mb-6">
                    <SubmissionProgress
                        submissionProgress={submissionProgress}
                        isSubmitting={isSubmitting}
                    />
                </div>
            )}

            {submissionResult ? (
                <SubmissionResult submissionResult={submissionResult} />
            ) : !isSubmitting && (
                <div className="text-center py-12 text-muted-foreground bg-accent">
                    <FiSend className="text-5xl mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">Ready to Submit</h3>
                    <p className="text-muted-foreground mb-1">Submit your solution to see results</p>
                    <p className="text-sm text-muted-foreground">
                        Your code will be tested against all hidden test cases
                    </p>
                </div>
            )}
        </div>
    );
};

/**
 * @component SubmissionProgress
 * @description Real-time progress bar with passed/failed counters during batch grading.
 */
const SubmissionProgress = ({ submissionProgress }) => {
    const isError = submissionProgress.status === 'failed' || submissionProgress.status === 'error';
    const isCompleted = submissionProgress.status === 'completed';

    return (
        <div className="bg-accent border-b border-gray-300 py-4">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground flex items-center space-x-2">
                    <span>Submission Progress</span>
                </h4>
                <span className="text-sm text-muted-foreground">
                    {isError && submissionProgress.current > 0
                        ? `${submissionProgress.passedTests}/${submissionProgress.total}`
                        : `${submissionProgress.current}/${submissionProgress.total}`
                    } tests
                </span>
            </div>

            <div className="w-full bg-gray-300 rounded-full h-1 mb-3 relative overflow-hidden">
                <div
                    className={`h-1 rounded-full relative transition-all duration-500 ${isError ? 'bg-red-600' :
                        isCompleted ? 'bg-emerald-600' :
                            'bg-gradient-to-r from-emerald-600 to-blue-600'
                        }`}
                    style={{
                        width: submissionProgress.total > 0
                            ? `${(submissionProgress.passedTests / submissionProgress.total) * 100}%`
                            : '0%'
                    }}
                />

                {submissionProgress.status === 'running' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                )}
            </div>

            <div className="flex items-center justify-between text-sm">
                <span className="text-foreground font-medium">{submissionProgress.message}</span>
                <div className="flex items-center space-x-3">
                    {submissionProgress.passedTests > 0 && (
                        <span className="text-green-600 flex items-center space-x-1">
                            <FiCheck className="w-3 h-3" />
                            <span>{submissionProgress.passedTests}</span>
                        </span>
                    )}
                    {submissionProgress.failedTests > 0 && (
                        <span className="text-red-600 flex items-center space-x-1">
                            <FiX className="w-3 h-3" />
                            <span>{submissionProgress.failedTests}</span>
                        </span>
                    )}
                </div>
            </div>

            {submissionProgress.failedTests > 0 && SubmissionResult.is_test_mode_result && (
                <div className="mt-3 bg-red-600 text-white px-4 py-2 rounded text-xs font-semibold flex items-center space-x-2">
                    <FiX className="text-white w-4 h-4" />
                    <span>OOPS!! some test cases failed please modify your approach and logic...</span>
                </div>
            )}
        </div>
    );
};

/**
 * @component SubmissionResult
 * @description Displays the final submission verdict: accepted stats grid, compilation errors, or failed test details.
 */
const SubmissionResult = ({ submissionResult }) => {
    const isAccepted = submissionResult.status === 'Accepted' || submissionResult.status.includes('🎉');

    const isCompilationError = submissionResult.status?.includes('Compilation Error') ||
        submissionResult.status?.includes('System Error') ||
        submissionResult.status?.includes('Runtime Error') ||
        (submissionResult.failureDetails && submissionResult.failureDetails.error &&
            (submissionResult.failureDetails.error.includes('Compilation') ||
                submissionResult.failureDetails.error.includes('Runtime') ||
                submissionResult.failureDetails.error.includes('System')));

    const [copied, setCopied] = useState(false);

    const copyTestCase = async () => {
        const testCaseData = `Input:
${submissionResult.failureDetails.input}
    
Expected:
${submissionResult.failureDetails.expected}
    
Your Output:
${submissionResult.failureDetails.actual || 'No output'}`;

        try {
            await navigator.clipboard.writeText(testCaseData);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
        }
    };

    if (isCompilationError) {
        return (
            <div className="bg-white border-l-4 border-red-600 p-4 rounded">
                <div className="flex items-center space-x-2 mb-3">
                    {/* <FiAlertTriangle className="text-red-600 text-lg" /> */}
                    <h4 className="font-medium text-red-600">
                        {submissionResult.status?.includes('Compilation') ? 'Compilation Error' :
                            submissionResult.status?.includes('Runtime') ? 'Runtime Error' :
                                submissionResult.failureDetails?.error?.includes('Compilation') ? 'Compilation Error' :
                                    submissionResult.failureDetails?.error?.includes('Runtime') ? 'Runtime Error' :
                                        'Execution Error'}
                    </h4>
                </div>
                <div className="font-mono text-sm text-red-600 text-left whitespace-pre-wrap">
                    {submissionResult.message || submissionResult.failureDetails?.error || submissionResult.status}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="text-center">
                {/* Stats grid: only show for accepted or test-mode results */}
                {isAccepted && submissionResult.passedTests !== undefined && submissionResult.totalTests !== undefined && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white border-l-4 border-emerald-600 p-3 rounded shadow-sm">
                            <div className="text-2xl font-bold text-foreground mb-1">
                                {submissionResult.passedTests}/{submissionResult.totalTests}
                            </div>
                            <div className="text-sm text-muted-foreground">Test Cases</div>
                        </div>

                        {submissionResult.executionTime && (
                            <div className="bg-white border-l-4 border-emerald-600 p-3 rounded shadow-sm">
                                <div className="text-2xl font-bold text-foreground mb-1">
                                    {submissionResult.executionTime}
                                </div>
                                <div className="text-sm text-muted-foreground">Execution Time</div>
                            </div>
                        )}

                        {submissionResult.successRate && (
                            <div className="bg-white border-l-4 border-emerald-600 p-3 rounded shadow-sm">
                                <div className="text-2xl font-bold text-foreground mb-1">
                                    {submissionResult.successRate}
                                </div>
                                <div className="text-sm text-muted-foreground">Success Rate</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Fallback Result Message if no grid or failure details */}
                {submissionResult.passedTests === undefined && !submissionResult.failureDetails && (
                    <div className={`bg-white border-l-4 p-6 mt-4 rounded shadow-sm ${isAccepted ? 'border-emerald-600' : 'border-red-600'}`}>
                        <h3 className={`text-xl font-bold mb-2 ${isAccepted ? 'text-emerald-600' : 'text-red-600'}`}>
                            {submissionResult.status || 'Submission Result'}
                        </h3>
                        <p className="text-foreground">
                            {submissionResult.message || 'The evaluation has completed.'}
                        </p>
                    </div>
                )}

                {/* Failed test case: show as foldable bar (same style as sample test results) */}
                {submissionResult.failureDetails && !isAccepted && !submissionResult.is_test_mode_result && (
                    <div className="text-left">
                        <TestCaseResult
                            result={{
                                input: submissionResult.failureDetails.input,
                                expected: submissionResult.failureDetails.expected,
                                actual: submissionResult.failureDetails.actual || 'No output',
                                passed: false,
                                executionTime: submissionResult.executionTime || null
                            }}
                            index={submissionResult.failureDetails.testCase ? submissionResult.failureDetails.testCase - 1 : 0}
                        />
                    </div>
                )}
            </div>

            {/* Clean Error Detail Box - Only show if not a simple compilation error and not a test result */}
            {submissionResult.error && !isCompilationError && !submissionResult.is_test_mode_result && (
                <div className="mt-8 pt-4 border-t border-gray-300">
                    <div className="flex items-center space-x-2 text-red-600 mb-3">
                        <FiAlertTriangle className="text-red-600" />
                        <span className="text-sm font-semibold uppercase tracking-wider">Error Insight</span>
                    </div>
                    <div className="bg-white border-l-4 border-red-600 p-4 rounded font-mono text-xs text-red-600 overflow-x-auto whitespace-pre-wrap shadow-sm">
                        {submissionResult.error}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedTestResultsPanel;